import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { SystemCollections } from 'components/dittoAppConnection'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useState } from 'react'
import { executeLegacyFindQuery } from 'utils'
import { queryClient } from 'utils/queryClient'

import useDeviceRPC, { Device, resultToDevice } from './useDeviceRPC'

dayjs.extend(utc)

export type Params = {
  /**
   * The ID of the app to fetch devices for
   */
  appId: string
  /**
   * The cloud domain URL for the app
   */
  cloudUrl?: string
}

type Return = {
  /**
   * The access token used for RPC requests
   */
  accessToken?: string
  /**
   * Whether the devices are currently being fetched
   */
  isLoading: boolean
  /**
   * Whether the devices are currently being refetched
   */
  isRefetching: boolean
  /**
   * The devices data
   */
  devices: Device[]
  /**
   * An error message if an error occurred while fetching the devices
   */
  errorMessage?: string
  /**
   * A callback to fetch the logs for a device, provided a specific attachment ID
   */
  fetchLogs: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to refetch the devices
   */
  refetch: () => Promise<void>
  /**
   * A callback to request logs for a device, provided its peer key.
   * This will refresh the devices list after the logs request is made.
   */
  requestLogs: (peerKey: string) => Promise<void>
  /**
   * A callback to request logs for a list of devices, provided their peer keys.
   * This will refresh the devices list after the logs request is made.
   */
  bulkRequestLogs: (peerKeys: string[]) => Promise<void>
}

export const BASE_QUERY_KEY = 'getDevices'

/** A hook that provides device dashboard data and log requests operations. */
export default function useDevices({ appId, cloudUrl }: Params): Return {
  const {
    accessToken,
    getDevices,
    requestLogs,
    fetchLogs,
    bulkRequestLogs,
    refreshToken,
  } = useDeviceRPC({ appId, cloudUrl })

  const {
    data: devices,
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useQuery([BASE_QUERY_KEY, appId], () => getDevices(), {
    enabled: !!accessToken,
  })
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [didRetry, setDidRetry] = useState(false)

  const handleTryAgain = useCallback(async () => {
    setDidRetry(true)
    await refreshToken()
    await refetch()
  }, [refreshToken, refetch])

  /**
   * An effect to properly handle errors when fetching the device. This will
   * primarily serve to set the error message, with the exception of 401 errors.
   */
  useEffect(() => {
    if (!error) return

    const fallback = 'An error occurred while fetching devices'
    if (isAxiosError(error)) {
      if (error.response?.status === 401 && !didRetry) {
        handleTryAgain()
      } else {
        setErrorMessage(error.response?.data?.message || fallback)
      }
    } else if (error instanceof Error) {
      setErrorMessage(error.message || fallback)
    } else {
      setErrorMessage(fallback)
    }
  }, [error, handleTryAgain, didRetry])

  const handleRequestLogs = useCallback(
    async (peerKey: string) => {
      await requestLogs(peerKey)
      await refetch()
    },
    [requestLogs, refetch],
  )

  const handleBulkRequestLogs = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestLogs(peerKeys)
      await refetch()
    },
    [bulkRequestLogs, refetch],
  )

  const handleRefetch = useCallback(async () => {
    await refetch()
  }, [refetch])

  return {
    accessToken,
    devices: devices || [],
    isLoading,
    isRefetching,
    errorMessage,
    fetchLogs,
    requestLogs: handleRequestLogs,
    bulkRequestLogs: handleBulkRequestLogs,
    refetch: handleRefetch,
  }
}

/**
 * A function to fetch a specific set of devices by their peer keys.
 *
 * Note that the cache key for this query is not the same as the one used in the hook.
 * This is because they serve different purposes, primarily that this function will be used
 * for cherry-picking devices which are awaiting log uploads in order to poll and update
 * the hook's cache with the latest data.
 */
export const fetchDevicesByPeerKeys = async (
  { appId, cloudUrl }: Required<Params>,
  peerKeys: string[],
  accessToken: string,
) => {
  const res = await queryClient.fetchQuery(
    [BASE_QUERY_KEY, appId, peerKeys],
    () =>
      executeLegacyFindQuery(
        {
          query: 'contains($args.ids, _id)',
          args: {
            ids: peerKeys,
          },
          collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
          options: {
            formatAttachment: true,
          },
        },
        cloudUrl,
        appId,
        accessToken,
      ),
  )

  return res.documents.map(resultToDevice)
}
